import { leadsEndpoints } from "@/config/endpoints";
import axiosInstance from "@/lib/axios";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { invalidateLeadBoard, invalidateLeadTimeline } from "@/lib/queryClient";
import {
  showBulkToast,
  showCompletedBulkPill,
} from "@/features/leads/components/bulk-update/BulkOperationPill";

export interface ArchivedLead {
  id: number;
  name: string;
  phone: string | null;
  /** `+<calling code>` split off by the server; older payloads may omit it. */
  phoneCountryCode?: string | null;
  email: string | null;
  archivedAt: string | null;
  createdAt: string;
  isArchived: boolean;
  branch?: { id: number; name: string } | null;
  leadSource?: { id: number; name: string; type?: string } | null;
  campaign?: { id: number; name: string } | null;
  archivedBy?: { id: number; name: string; email: string } | null;
  LeadUsers?: { user?: { id: number; name: string } | null }[];
}

interface ArchivedListResponse {
  status: string;
  leads: ArchivedLead[];
  total?: any;
  meta?: { page?: number; total?: number; totalPages?: number };
}

interface UseArchivedLeadsParams {
  page?: number;
  limit?: number;
  search?: string;
}

export const ARCHIVED_LEADS_KEY = ["archived-leads"] as const;

export function useArchivedLeads(params: UseArchivedLeadsParams = {}) {
  const { page = 1, limit = 20, search = "" } = params;

  return useQuery({
    queryKey: [...ARCHIVED_LEADS_KEY, { page, limit, search }],
    queryFn: async () => {
      const { data } = await axiosInstance.get<ArchivedListResponse>(
        leadsEndpoints,
        {
          params: {
            archived: "true",
            page,
            limit,
            search: search || undefined,
          },
        }
      );
      return data;
    },
  });
}

function invalidateArchiveAndLeads(
  queryClient: ReturnType<typeof useQueryClient>
) {
  queryClient.invalidateQueries({ queryKey: ["archived-leads"] });
  // List/board + header count/stats tiles.
  invalidateLeadBoard();
  queryClient.invalidateQueries({ queryKey: ["get-leads"] });
  // Archive/restore writes an activity row; a drawer left open after a
  // restore used to catch it via our own socket echo.
  invalidateLeadTimeline();
}

interface BulkArchivePayload {
  leads: number[];
  archive: boolean;
}

export function useBulkArchive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ leads, archive }: BulkArchivePayload) => {
      const { data } = await axiosInstance.post(
        `${leadsEndpoints}/bulk-archive`,
        { leads, archive }
      );
      return data as {
        status: string;
        mode?: "sync" | "async";
        success?: number[];
        skipped?: { leadId: number; reason: string }[];
        failed?: { leadId: number; reason: string }[];
        totalRequested?: number;
        archive?: boolean;
        jobId?: string;
        totalLeads?: number;
      };
    },
    onSuccess: (res, vars) => {
      if (res.mode === "async") {
        // Progress pill polls the job and refreshes caches on completion.
        showBulkToast(res.jobId, {
          kind: vars.archive ? "archive" : "restore",
          total: res.totalLeads ?? vars.leads.length,
        });
      } else {
        const count = res.success?.length ?? vars.leads.length;
        const skipped = res.skipped?.length ?? 0;
        // Sync branch — same pill surface as the async path, popped done.
        showCompletedBulkPill(
          {
            kind: vars.archive ? "archive" : "restore",
            total: vars.leads.length,
          },
          {
            done: count,
            subtitle: skipped > 0 ? `${skipped} skipped` : undefined,
          },
        );
      }
      invalidateArchiveAndLeads(queryClient);
    },
    onError: (err: any, vars) => {
      const action = vars.archive ? "archive" : "restore";
      const msg =
        err?.response?.data?.message || `Failed to ${action} leads`;
      toast.error(msg);
    },
  });
}

export function useArchiveOne() {
  const bulk = useBulkArchive();
  return {
    ...bulk,
    archive: (leadId: number) =>
      bulk.mutateAsync({ leads: [leadId], archive: true }),
    restore: (leadId: number) =>
      bulk.mutateAsync({ leads: [leadId], archive: false }),
  };
}
